import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { Button, Col, Row } from 'reactstrap';
import Content from '../../../layout/Content';
import Table from '../../../common/Table';
import CabinCrewTaskSummaryBaseForm, {
  CABIN_CREW_TASK_SUMMARY_BASE_FORM,
} from './CabinCrewTaskSummaryBaseForm';
import CabinCrewTaskSummaryMovementsForm from './CabinCrewTaskSummaryMovementsForm';
import CabinCrewTaskSummaryMonthlyForm from './CabinCrewTaskSummaryMonthlyForm';
import {
  CABIN_CREW_TASK_SUMMARY_MONTHLY_COLUMNS,
  TASK_SUMMARY_MOVEMENT_COLUMNS,
} from '../../../../config/columns';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import { CABIN_CREW_TASK_SUMMARY_PATH } from '../../../../config/paths';
import {
  clearCabinCrewTaskSummary,
  exportCabinCrewTaskSummaryMonthly,
  getCabinCrewTaskSummaryMonthly,
  getCabinCrewTaskSummaryMovements,
} from '../../../../actions/traffic/CabinCrewTaskSummary';
import SummaryGauge from '../../../common/gauge/SummaryGauge';
import '../../../common/gauge/GaugeChart.css';
import { filterMethodCaseInsensitive } from '../../../../utils/filter-methods';

const baseSelector = formValueSelector(CABIN_CREW_TASK_SUMMARY_BASE_FORM);

const buildSelectValues = (options) =>
  options && options.length > 0 ? options.map((option) => option.value) : null;

const buildCommonFilters = (homeBaseLocation, cabinCrewStatusType) => {
  const filters = {};

  const homeBaseLocationIds = buildSelectValues(homeBaseLocation);
  if (homeBaseLocationIds) {
    filters.homeBaseLocationIds = homeBaseLocationIds;
  }

  const cabinCrewStatusTypes = buildSelectValues(cabinCrewStatusType);
  if (cabinCrewStatusTypes) {
    filters.cabinCrewStatusTypes = cabinCrewStatusTypes;
  }

  return filters;
};

const CabinCrewTaskSummary = ({
  breadcrumbs,
  homeBaseLocation,
  cabinCrewStatusType,
  movements,
  monthly,
  loadingMovements,
  loadingMonthly,
  dispatchGetCabinCrewTaskSummaryMovements,
  dispatchGetCabinCrewTaskSummaryMonthly,
  dispatchClearCabinCrewTaskSummary,
  dispatchExportCabinCrewTaskSummaryMonthly,
}) => {
  const [lastMonthlyQuery, setLastMonthlyQuery] = useState(null);

  useEffect(
    () => () => {
      dispatchClearCabinCrewTaskSummary();
    },
    [],
  );

  const onMovementsSubmit = (formValues) => {
    const query = {
      fromDateString: formValues.fromDateString,
      toDateString: formValues.toDateString,
      ...buildCommonFilters(homeBaseLocation, cabinCrewStatusType),
    };

    dispatchGetCabinCrewTaskSummaryMovements(query);
  };

  const onMonthlySubmit = (formValues) => {
    const query = {
      fromMonthString: formValues.fromMonthString,
      ...buildCommonFilters(homeBaseLocation, cabinCrewStatusType),
    };

    if (formValues.toMonthString) {
      query.toMonthString = formValues.toMonthString;
    }

    setLastMonthlyQuery(query);
    dispatchGetCabinCrewTaskSummaryMonthly(query);
  };

  const movementsData = useMemo(
    () => (movements ? movements.toJS() : []),
    [movements],
  );

  const monthlyData = useMemo(() => (monthly ? monthly.toJS() : []), [monthly]);

  const movementsTotal = useMemo(
    () => movementsData.reduce((acc, item) => acc + (item.total || 0), 0),
    [movementsData],
  );

  const buildTotals = (rows) =>
    (rows || []).reduce(
      (acc, row) => ({
        vacations: acc.vacations + (row.vacations || 0),
        paidVacations: acc.paidVacations + (row.paidVacations || 0),
        generatedPhysicalRest:
          acc.generatedPhysicalRest + (row.generatedPhysicalRest || 0),
        takenPhysicalRest: acc.takenPhysicalRest + (row.takenPhysicalRest || 0),
      }),
      {
        vacations: 0,
        paidVacations: 0,
        generatedPhysicalRest: 0,
        takenPhysicalRest: 0,
      },
    );

  const totals = useMemo(() => buildTotals(monthlyData), [monthlyData]);

  const content = (
    <>
      <Row>
        <Col md="12">
          <h2>Base</h2>
          <CabinCrewTaskSummaryBaseForm />
        </Col>
      </Row>

      <hr />

      <Row>
        <Col>
          <h2>Resumen de Movimientos</h2>
          <CabinCrewTaskSummaryMovementsForm onSubmit={onMovementsSubmit} />
        </Col>
        <Col>
          <Table
            columns={TASK_SUMMARY_MOVEMENT_COLUMNS}
            data={movementsData}
            loading={loadingMovements}
            manual={false}
            showPagination={false}
            defaultPageSize={
              movementsData.length > 0 ? movementsData.length : 13
            }
          />

          {movementsData.length > 0 && (
            <div className="d-flex justify-content-between font-weight-bold border-top pt-2 mt-2">
              <span>Total</span>
              <span>{movementsTotal}</span>
            </div>
          )}
        </Col>
      </Row>

      <hr />

      <Row>
        <Col md="12">
          <h2>Resumen Mensual</h2>
          <CabinCrewTaskSummaryMonthlyForm onSubmit={onMonthlySubmit} />
          <Col className="d-flex justify-content-center">
            <Row className="flex-grow-1 justify-content-center">
              <Col md="6">
                <SummaryGauge
                  id="vacation-gauge"
                  title="Vacaciones"
                  value1={
                    totals.accumulatedVacations +
                    totals.accumulatedPaidVacations
                  }
                  value2={
                    totals.generatedVacationDays
                  }
                  loading={loadingMonthly}
                  legendFormatter={(value, total) =>
                    `${value} cumplida(s) de ${total} total`
                  }
                />
              </Col>

              <Col md="6">
                <SummaryGauge
                  id="physical-rest-gauge"
                  title="Descanso Físico"
                  value1={totals.takenPhysicalRest}
                  value2={
                    totals.generatedPhysicalRest - totals.takenPhysicalRest
                  }
                  loading={loadingMonthly}
                  legendFormatter={(value, total) =>
                    `${value} gozado(s) de ${total} generado(s)`
                  }
                />
              </Col>
            </Row>
          </Col>
          <div className="d-flex justify-content-end mb-3">
            <Button
              color="success"
              size="md"
              className="flex-end"
              disabled={!lastMonthlyQuery || loadingMonthly}
              onClick={() =>
                dispatchExportCabinCrewTaskSummaryMonthly(lastMonthlyQuery)
              }
            >
              <i className="fa fa-file-excel-o mr-2" />
              Exportar a Excel
            </Button>
          </div>
          <Table
            columns={CABIN_CREW_TASK_SUMMARY_MONTHLY_COLUMNS}
            data={monthlyData}
            loading={loadingMonthly}
            manual={false}
            filterable
            defaultPageSize={20}
            defaultFilterMethod={filterMethodCaseInsensitive}
          />
        </Col>
      </Row>
    </>
  );

  return (
    <Content
      title="Resumen de Tareo Servicio a Bordo"
      breadcrumbs={breadcrumbs}
      content={content}
      subtitle="Los filtros base y estado afecta a toda la vista"
    />
  );
};

CabinCrewTaskSummary.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  homeBaseLocation: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    }),
  ),
  cabinCrewStatusType: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  movements: PropTypes.instanceOf(Immutable.List),
  monthly: PropTypes.instanceOf(Immutable.List),
  loadingMovements: PropTypes.bool,
  loadingMonthly: PropTypes.bool,
  dispatchGetCabinCrewTaskSummaryMovements: PropTypes.func.isRequired,
  dispatchGetCabinCrewTaskSummaryMonthly: PropTypes.func.isRequired,
  dispatchClearCabinCrewTaskSummary: PropTypes.func.isRequired,
  dispatchExportCabinCrewTaskSummaryMonthly: PropTypes.func.isRequired,
};

CabinCrewTaskSummary.defaultProps = {
  homeBaseLocation: [],
  cabinCrewStatusType: [],
  movements: null,
  monthly: null,
  loadingMovements: false,
  loadingMonthly: false,
};

const mapStateToProps = (state) => ({
  breadcrumbs: [
    ...state.TrafficUnit.CabinCrewUnitHome.get('breadcrumbs'),
    {
      text: 'Resumen de Tareo Servicio a Bordo',
      href: CABIN_CREW_TASK_SUMMARY_PATH,
    },
  ],
  homeBaseLocation: baseSelector(state, 'homeBaseLocation'),
  cabinCrewStatusType: baseSelector(state, 'cabinCrewStatusType'),
  movements: state.TrafficUnit.CabinCrewTaskSummary.getIn([
    'movements',
    'content',
  ]),
  monthly: state.TrafficUnit.CabinCrewTaskSummary.getIn(['monthly', 'content']),
  loadingMovements: state.TrafficUnit.CabinCrewTaskSummary.getIn([
    'movements',
    'loading',
  ]),
  loadingMonthly: state.TrafficUnit.CabinCrewTaskSummary.getIn([
    'monthly',
    'loading',
  ]),
});

const mapDispatchToProps = {
  dispatchGetCabinCrewTaskSummaryMovements: getCabinCrewTaskSummaryMovements,
  dispatchGetCabinCrewTaskSummaryMonthly: getCabinCrewTaskSummaryMonthly,
  dispatchClearCabinCrewTaskSummary: clearCabinCrewTaskSummary,
  dispatchExportCabinCrewTaskSummaryMonthly: exportCabinCrewTaskSummaryMonthly,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CabinCrewTaskSummary);
